import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FaLock, FaUser, FaDownload, FaEye, FaSignOutAlt, FaTimesCircle, FaTrash, FaExclamationTriangle, FaCertificate, FaChartPie, FaRobot } from 'react-icons/fa';
import CertificateManagement from '../components/CertificateManagement';
import DetailedReports from '../components/DetailedReports';
import NovayarAdminPanel from '../components/NovayarAdminPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, registration: null });
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('registrations');
  const [selectedFestival, setSelectedFestival] = useState('all'); // all, omid, shomal_ai_2025

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setToken('');
    setRegistrations([]);
    setEmail('');
    setPassword('');
  }, []);

  const loadRegistrations = useCallback(async (accessToken, festivalFilter = 'all') => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/registrations`;
      if (festivalFilter !== 'all') {
        url += `?festival_id=${festivalFilter}`;
      }
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error loading registrations:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  // Check if already logged in
  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      loadRegistrations(savedToken, selectedFestival);
    }
  }, [loadRegistrations, selectedFestival]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        email,
        password
      });

      const accessToken = response.data.access_token;
      setToken(accessToken);
      localStorage.setItem('adminToken', accessToken);
      setIsLoggedIn(true);
      loadRegistrations(accessToken, selectedFestival);
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'خطا در ورود. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export-excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrations_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('خطا در دانلود فایل اکسل');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteClick = (registration) => {
    setDeleteModal({ isOpen: true, registration });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.registration) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/registrations/${deleteModal.registration.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update local state
      setRegistrations(prev => prev.filter(r => r.id !== deleteModal.registration.id));
      setDeleteModal({ isOpen: false, registration: null });
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert('خطا در حذف ثبت‌نام');
    } finally {
      setDeleting(false);
    }
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-700 to-primary-600 p-8 text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <FaLock className="text-4xl text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">پنل مدیریت</h1>
              <p className="text-white/80">سامانه نوآفرین‌شو</p>
            </div>

            {/* Form */}
            <div className="p-8">
              {loginError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border-2 border-red-500 rounded-xl flex items-center gap-3 text-red-900 dark:text-red-300"
                >
                  <FaTimesCircle className="text-2xl flex-shrink-0" />
                  <span className="font-semibold">{loginError}</span>
                </motion.div>
              )}

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300">
                    ایمیل
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full p-4 pr-12 bg-gray-50 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all"
                      placeholder="ایمیل خود را وارد کنید"
                      data-testid="admin-email"
                    />
                    <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300">
                    رمز عبور
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full p-4 pr-12 bg-gray-50 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all"
                      placeholder="رمز عبور خود را وارد کنید"
                      data-testid="admin-password"
                    />
                    <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className={`w-full py-4 bg-gradient-to-r from-primary-700 to-primary-600 text-white rounded-xl font-bold text-lg shadow-xl transition-all ${
                    loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-2xl'
                  }`}
                  data-testid="login-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      در حال ورود...
                    </span>
                  ) : (
                    'ورود به پنل'
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-primary-800 dark:text-primary-400 mb-2">
                پنل مدیریت
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                مدیریت ثبت‌نام‌های جشنواره امید
              </p>
            </div>
            <div className="flex gap-3">
              <motion.button
                onClick={handleExportExcel}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-600 to-accent-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                data-testid="export-excel-btn"
              >
                <FaDownload />
                دانلود اکسل
              </motion.button>
              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold shadow-lg transition-all"
              >
                <FaSignOutAlt />
                خروج
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Festival Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden mb-8"
        >
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setSelectedFestival('all')}
              className={`flex-1 px-6 py-4 font-bold text-base transition-all whitespace-nowrap ${
                selectedFestival === 'all'
                  ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              همه جشنواره‌ها
            </button>
            <button
              onClick={() => setSelectedFestival('shomal_ai_2025')}
              className={`flex-1 px-6 py-4 font-bold text-base transition-all whitespace-nowrap ${
                selectedFestival === 'shomal_ai_2025'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Shomal AI Connect 2025
            </button>
            <button
              onClick={() => setSelectedFestival('omid')}
              className={`flex-1 px-6 py-4 font-bold text-base transition-all whitespace-nowrap ${
                selectedFestival === 'omid'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              جشنواره امید (پایان یافته)
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid md:grid-cols-3 gap-6 mb-8"
        >
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white shadow-xl">
            <div className="text-4xl font-bold mb-2">{registrations.length}</div>
            <div className="text-white/90">
              {selectedFestival === 'all' ? 'کل ثبت‌نام‌ها' : 'ثبت‌نام‌های این جشنواره'}
            </div>
          </div>
          <div className="bg-gradient-to-br from-secondary-600 to-secondary-500 rounded-2xl p-6 text-gray-900 shadow-xl">
            <div className="text-4xl font-bold mb-2">
              {registrations.filter(r => new Date(r.created_at).toDateString() === new Date().toDateString()).length}
            </div>
            <div className="text-gray-800">ثبت‌نام امروز</div>
          </div>
          <div className="bg-gradient-to-br from-accent-600 to-accent-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="text-4xl font-bold mb-2">
              {registrations.filter(r => {
                const date = new Date(r.created_at);
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return date >= weekAgo;
              }).length}
            </div>
            <div className="text-white/90">ثبت‌نام هفته اخیر</div>
          </div>
        </motion.div>

        {/* Content Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden mb-8"
        >
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('registrations')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold text-lg transition-all whitespace-nowrap ${
                activeTab === 'registrations'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FaEye />
              لیست ثبت‌نام‌ها
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold text-lg transition-all whitespace-nowrap ${
                activeTab === 'reports'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid="detailed-reports-tab"
            >
              <FaChartPie />
              گزارش تفکیکی
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold text-lg transition-all whitespace-nowrap ${
                activeTab === 'certificates'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FaCertificate />
              مدیریت گواهی‌ها
            </button>
            <button
              onClick={() => setActiveTab('novayar')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold text-lg transition-all whitespace-nowrap ${
                activeTab === 'novayar'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FaRobot />
              نوآیار
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        {activeTab === 'registrations' ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-3">
                <FaEye className="text-primary-600" />
                لیست ثبت‌نام‌ها
              </h2>
            </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              </div>
            ) : registrations.length === 0 ? (
              <div className="text-center p-12 text-gray-500 dark:text-gray-400">
                <p className="text-xl">هیچ ثبت‌نامی وجود ندارد</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">ردیف</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">تاریخ</th>
                    {selectedFestival === 'all' && (
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">جشنواره</th>
                    )}
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">نام هسته</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">نام</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">موبایل</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">عنوان ایده</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">دسته</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-300">TRL</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 dark:text-gray-300">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {registrations.map((reg, index) => (
                    <motion.tr
                      key={reg.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200">{index + 1}</td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm whitespace-nowrap">
                        {formatDate(reg.created_at)}
                      </td>
                      {selectedFestival === 'all' && (
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${
                            reg.festival_id === 'shomal_ai_2025' 
                              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-400'
                              : reg.festival_id === 'omid'
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400'
                          }`}>
                            {reg.festival_id === 'shomal_ai_2025' ? 'AI Connect' : reg.festival_id === 'omid' ? 'امید' : reg.festival_id}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200 font-medium">
                        {reg.tech_core_name}
                      </td>
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200">
                        {reg.first_name} {reg.last_name}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400 font-mono text-left">
                        {reg.mobile}
                      </td>
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200 max-w-xs truncate">
                        {reg.idea_title}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                        {reg.category_main}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center justify-center px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400 rounded-lg font-bold text-sm">
                          {reg.trl_level}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <motion.button
                            onClick={() => handleDeleteClick(reg)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg transition-all"
                            title="حذف ثبت‌نام"
                            data-testid={`delete-btn-${reg.id}`}
                          >
                            <FaTrash className="text-sm" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </motion.div>
        ) : activeTab === 'reports' ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6"
          >
            <DetailedReports token={token} />
          </motion.div>
        ) : activeTab === 'certificates' ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6"
          >
            <CertificateManagement token={token} />
          </motion.div>
        ) : activeTab === 'novayar' ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6"
          >
            <NovayarAdminPanel token={token} />
          </motion.div>
        ) : null}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !deleting && setDeleteModal({ isOpen: false, registration: null })}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 dark:from-red-700 dark:to-red-800 px-6 py-5">
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <FaExclamationTriangle className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">تایید حذف</h3>
                      <p className="text-white/80 text-sm">این عملیات قابل بازگشت نیست</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                    آیا از حذف این ثبت‌نام اطمینان دارید؟
                  </p>
                  
                  {deleteModal.registration && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">نام هسته:</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {deleteModal.registration.tech_core_name}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">نام:</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {deleteModal.registration.first_name} {deleteModal.registration.last_name}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">عنوان ایده:</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200 truncate max-w-[200px]">
                          {deleteModal.registration.idea_title}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col-reverse sm:flex-row gap-3">
                    <button
                      onClick={() => setDeleteModal({ isOpen: false, registration: null })}
                      disabled={deleting}
                      className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
                    >
                      انصراف
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={deleting}
                      className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                          در حال حذف...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <FaTrash />
                          حذف ثبت‌نام
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Admin;
